import I18n from "../base/i18n.js";
import { snakeCase } from "../utils/utils.js";

/**
 * Class, described entity for tables, forms and displaying it on frontend
 * 
 * When you create inheritance of base model, you must create and name it as Raw<YourModelName> and
 * at the end of file write code like this:
 * ```
 * const <YourModelName> = new Raw<YourModelName>()
 * export { Raw<YourModelName>, <YourModelName> }
 * ```
 * 
 * After that, you can use Raw<YourModelName> as constructor for config fields for specific cases and 
 * <YourModelName> as model for common cases.
 * @name BaseModel
 * @class
 * @abstract
 * @param {Object} [params = {}]
 * @param {Object} [params.listFieldsConfig = {}] Configuration for list columns. Overrides properties like `type`,
 * `displayName` etc.
 * ```
 * Example of list fields config (it will override properties of field "name")
 * const listFieldsConfig = {
 *  name: {
 *   displayName: "Updated name"
 *  }
 * }
 * ```
 * @param {Object} [params.formFieldsConfig = {}] Configuration for form fields. Overrides properties like `type`,
 * `displayName` etc.
 * ```
 * Example of form fields config (it will override properties of field "name")
 * const formFieldsConfig = {
 *  name: {
 *   displayName: "Updated name",
 *   required: true,
 *   displayNamePosition: InputLabelDisplayModeAbove
 *  }
 * }
 * ```
*/
export default class BaseModel {

  constructor({listFieldsConfig = {}, formFieldsConfig = {}} = {}) {
    this.listFieldsConfig = listFieldsConfig;
    if(!listFieldsConfig) {
      this.listFieldsConfig = {};
    }

    this.formFieldsConfig = formFieldsConfig;
    if(!formFieldsConfig) {
      this.formFieldsConfig = {};
    }
  }

  /**
   * Method for creating list of model filters. Works on methods `listFiltersWhitelist()` and `rawListFilters()`
   * @returns {Array.<FormFieldDescription>}
   * @example
   * //Filters for common case
   * class RawItemModel extends BaseModel {
   * rawListFilters() {
   *  return [
   *    {name: "status"},
   *    {name: "type"},
   *    {name: "provider"}
   *  ]
   * }
   * ...
   * //In special case, filter by provider not necessary
   * class SpecialItemModel extends RawItemModel {
   *  listFiltersWhitelist() {
   *    return ["status", "type"]
   *  }
   * }
   */
  listFilters() {
    let fields = this.rawListFilters();
    let whitelist = this.listFiltersWhitelist();
    if(whitelist) {
      fields = fields.filter(field => whitelist.includes(field.name));
    }

    return fields;
  }

  /**
   * Method for define allowed filters in inheritance of Raw<YourModel>
   * @returns {String} names of allowed filters
   */
  listFiltersWhitelist() {
    return undefined;
  }

  /**
   * All possible filters for model
   * @returns {Array.<FormFieldDescription>}
   */
  rawListFilters() {
    return [];
  }

  /**
   * Method for creating list of cells for tables. Works in 3 phases:
   *  - fetching all possible table cells with method `rawListFields()`
   *  - filtering allowed cells with method `listFieldWhitelist()`
   *  - applying extra parameters for cells with `listFieldParams(cellParams)`
   * @returns {Array.<TableCellDescription>}
   * @example
   * //Cells for common case
   * class RawItemModel extends BaseModel {
   * rawListFields() {
   *  return [
   *    {name: "id"},
   *    {name: "name"},
   *    {name: "createdAt"}
   *  ]
   * }
   * ...
   * //In special case, filter by provider not necessary
   * class SpecialItemModel extends RawItemModel {
   *  listFieldWhitelist() {
   *    return ["id", "name"]
   *  }
   * }
   */

  listFields() {
    let fields = this.rawListFields();
    let whitelist = this.listFieldWhitelist();
    if(whitelist) {
      fields = fields.filter(field => whitelist.includes(field.name));
    }

    return fields.map((field) => {
      return {...field, ...this.listFieldParams(field.name)}
    }); 
  }

  /**
   * Method for define allowed columns in inheritance of Raw<YourModel>
   * @returns {String} names of allowed columns
   */
  listFieldWhitelist() {
    return undefined;
  }

  /**
   * All possible columns of model
   * @returns {Array.<TableCellDescription>}
   */
  rawListFields() {
    return [];
  }

  /**
   * Method for creating list of fields for forms. Works in 3 phases:
   *  - fetching all possible form fields with method `rawFormFields()`
   *  - filtering allowed fields with method `formFieldsWhitelist()`
   *  - filter only visible for item fields (by `visibleFunction`'s in even field)
   *  - fill field's validations (required, limits of symbols etc.) from `validator`'s hash
   *  - applying extra parameters for fields with `formFieldParams(cellParams)`
   * @param {Object} [params = {}]
   * @param {Object} [params.item = undefined] item for display in form
   * @param {Object} [params.validations = {}] fetched from server validations
   * @returns {Array.<FormFieldDescription>}
   * @example
   * //Fields for common case
   * class RawItemModel extends BaseModel {
   * rawFormFields() {
   *  return [
   *    {name: "id"},
   *    {name: "name"},
   *    {name: "createdAt"}
   *  ]
   * }
   * ...
   * //In special case, filter by provider not necessary
   * class SpecialItemModel extends RawItemModel {
   *  formFieldsWhitelist() {
   *    return ["id", "name"]
   *  }
   * }
   */
  formFields({item = undefined, validations = {}, config = {}} = {}) {
    let fields = this.rawFormFields(item, config);
    let whitelist = this.formFieldsWhitelist(item);
    if(whitelist) {
      fields = fields.filter(field => whitelist.includes(field.name));
    }

    fields = fields.filter((field) => {
      if(!field.visibleFunction) {
        return true;
      }
      return field.visibleFunction(item);
    });
    
    fields = this.formFieldsWithValidations(fields, validations);

    return fields.map((field) => {
      return {...field, ...this.formFieldParams(field.name)}
    });
  }

  /**
   * Method for define allowed fields in inheritance of Raw<YourModel>
   * @returns {String} names of allowed fields
   */
  formFieldsWhitelist() {
    return undefined;
  }

  /**
   * Method for processing and field field's validations
   * @param {Array.<FormFieldDescription>} fields form fields without validations
   * @param {Object} validations fetched from server validations
   * @returns {Array.<FormFieldDescription>} processed form fields
   */
  formFieldsWithValidations(fields, validations) {
    return fields.map((field) => {
      let fieldValidations = validations[field.name];
      if(!fieldValidations) {
        return field;
      }
      if(fieldValidations.presence) {
        field.required = true;
      }
      if(field.type === "nestedModel" ||
         field.type === "nestedModelsArray") {
        field.nestedValidations = fieldValidations;
      }
      return field;
    });
  }

  /**
   * All possible fields of model
   * @param {Object} [item = undefined] displaying in form entity
   * @returns {Array.<FormFieldDescription>}
   */
  rawFormFields(item = undefined) {
    return [];
  }
  
  // TODO: Refactor nested fields
  nestedFormFields(parentField, {item = undefined} = {}) {
    let fields = this.rawNestedFormFields(item);
    let whitelist = this.formFieldsWhitelist(item);
    if(whitelist) {
      fields = fields.filter(field => whitelist.includes(field.name));
    }
    
    fields = this.formFieldsWithValidations(fields, parentField.nestedValidations || {});

    return fields.map((field) => {
      return {...field, ...this.formFieldParams(field.name)}
    });
  }

  rawNestedFormFields(item = undefined) {
    return [];
  }

  /**
   * Function for getting uniq value of item (id, slug, guid, etc.)
   * @param {Object} item item for getting value
   * @returns {*} uniq value of item
   */
  value(item) {
    if(!item) {
      return undefined;
    }
    return item.id;
  }

  /**
   * Function for getting display value of item (title, concatenated first name and last name etc.)
   * @param {Object} item item for getting value
   * @returns {String} display value
   */

  displayValue(item) {
    if(!item) {
      return "";
    }
    if(!item.name) {
      return "";
    }
    return item.name;
  }

  /**
   * Scope for translating field names
   * @returns {String} scope in format "some.path.to.model.in.yml"
   */
  i18nScope() {
    return "";
  }

  /**
   * Human readable field's name
   * @param {String} fieldName name of field
   * @returns {String} human readable field's name
   */
  fieldDisplayName(fieldName) {
    let snakeCasedName = snakeCase(fieldName);
    return I18n.t(`${this.i18nScope()}.fields.${snakeCasedName}`, {
      defaults: [{
        message: I18n.t(`model.shared.fields.${snakeCasedName}`)
      }]
    });
  }

  /**
   * Returns extra list field config by field name
   * @param {String} fieldName 
   * @returns {Object}
   */
  listFieldParams(fieldName) {
    if(!this.listFieldsConfig[fieldName]) {
      return {};
    }
    return this.listFieldsConfig[fieldName];
  }

  /**
   * Returns extra form field config by field name
   * @param {String} fieldName 
   * @returns {Object}
   */
  formFieldParams(fieldName) {
    if(!this.formFieldsConfig[fieldName]) {
      return {};
    }
    return this.formFieldsConfig[fieldName];
  }

}